import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ProgressStage {
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  message?: string;
}

export interface ProgressState {
  show: boolean;
  title: string;
  stages: ProgressStage[];
  currentStageIndex: number;
  progress: number; // 0-100
  currentMessage: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProgressIndicatorService {
  private state$ = new BehaviorSubject<ProgressState>({
    show: false,
    title: '',
    stages: [],
    currentStageIndex: 0,
    progress: 0,
    currentMessage: ''
  });

  /**
   * Get the current progress state as an observable
   */
  getState(): Observable<ProgressState> {
    return this.state$.asObservable();
  }

  /**
   * Show progress indicator with initial stages
   */
  show(title: string, stages: ProgressStage[]): void {
    this.state$.next({
      show: true,
      title,
      stages,
      currentStageIndex: 0,
      progress: 0,
      currentMessage: stages[0]?.name || ''
    });
  }

  /**
   * Update progress (0-100) and optionally update message
   */
  updateProgress(progress: number, message?: string): void {
    const current = this.state$.value;
    this.state$.next({
      ...current,
      progress,
      currentMessage: message || current.currentMessage
    });
  }

  /**
   * Update current stage status and message
   */
  updateStage(stageIndex: number, status: ProgressStage['status'], message?: string): void {
    const current = this.state$.value;
    const updatedStages = [...current.stages];

    if (updatedStages[stageIndex]) {
      updatedStages[stageIndex] = {
        ...updatedStages[stageIndex],
        status,
        message: message || updatedStages[stageIndex].message
      };
    }

    this.state$.next({
      ...current,
      stages: updatedStages,
      currentStageIndex: stageIndex
    });
  }

  /**
   * Move to next stage
   */
  nextStage(message?: string): void {
    const current = this.state$.value;
    const nextIndex = Math.min(current.currentStageIndex + 1, current.stages.length - 1);

    // Mark current stage as completed
    if (current.stages[current.currentStageIndex]) {
      this.updateStage(current.currentStageIndex, 'completed');
    }

    // Mark next stage as in-progress
    if (current.stages[nextIndex]) {
      this.updateStage(nextIndex, 'in-progress', message);
    }
  }

  /**
   * Mark current stage as error
   */
  setError(message: string): void {
    const current = this.state$.value;
    this.updateStage(current.currentStageIndex, 'error', message);
  }

  /**
   * Complete all stages and optionally close after delay
   */
  complete(autoCloseDelay?: number): void {
    const current = this.state$.value;
    const updatedStages = current.stages.map(stage => ({
      ...stage,
      status: 'completed' as const
    }));

    this.state$.next({
      ...current,
      stages: updatedStages,
      progress: 100,
      currentMessage: 'Completed!'
    });

    if (autoCloseDelay) {
      setTimeout(() => this.hide(), autoCloseDelay);
    }
  }

  /**
   * Hide the progress indicator
   */
  hide(): void {
    this.state$.next({
      show: false,
      title: '',
      stages: [],
      currentStageIndex: 0,
      progress: 0,
      currentMessage: ''
    });
  }
}
